/**
 * [BaselineItemConfigs] Detail/Edit view for a single item in a Baseline (Step 3B of Baseline workflow).
 * - Renders all editable fields and option/config controls for a selected baseline item.
 * - Uses local state for current item (cloned for editing), with validation/error states per field.
 * - Handles value changes, validation, create/update/copy/delete/archive actions, and photo management.
 * - Notifies parent of changes via onLocalItemChange, onSetDataChanged, onReloadCurrentItem, and onClearCurrentItem.
 * - Uses AddValueToSelectedOption for managing per-item configs/force structures.
 * - All DB operations go through Redux thunks; no direct Redux mutation.
 * - Follows RAVEN pattern: business/data logic is in handlers/hooks, UI logic only here.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.item - The item object to edit (deep-cloned from parent).
 * @param {string} props.mode - Controls editability ("edit", "view", etc.).
 * @param {string} [props.size] - UI size for text fields/selects.
 * @param {function(Object):void} props.onLocalItemChange - Callback to bubble up item changes.
 * @param {function(boolean):void} props.onSetDataChanged - Callback to notify parent of dirty state.
 * @param {function():void} props.onReloadBaseline - Callback to reload baseline after copy/delete.
 * @param {function(Object):void} props.onReloadCurrentItem - Callback to reload just the current item.
 * @param {function():void} props.onClearCurrentItem - Callback to return to the item list view.
 * @returns {React.ReactElement}
 */

import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useUpdateEffect } from "react-use";
import { useSelector } from "react-redux";
import validator from "validator";

// Custom hooks
import { useBaselinesData } from "src/hooks/useBaselinesData";
import { useBaselineItemActions } from "src/hooks/useBaselineItemActions";
import { useLookupsData } from "src/context/LookupsContext";
import { useDialogBox } from "src/hooks/useDialogBox";

// Layout/UI
import { NumericFormatAdapter } from "src/components/UI/common/NumericFormatAdapter";
import {
	Box,
	createListCollection,
	Field,
	HStack,
	Icon,
	Input,
	MetreaSelectContent as SelectContent,
	MetreaSelectItem as SelectItem,
	MetreaSelectLabel as SelectLabel,
	MetreaSelectRoot as SelectRoot,
	MetreaSelectTrigger as SelectTrigger,
	MetreaSelectValueText as SelectValueText,
	Portal,
	Stack,
	MetreaTooltip,
} from "@metrea-ui/v2";
import FormTextField from "src/components/UI/common/FormTextField";
import BaselineItemConfigPeriod from "src/components/UI/Input/TextfieldWithPeriod";
import AddValueToSelectedOption from "src/components/UI/AddValueToSelectedOption/AddValueToSelectedOption";
import PhotoManager from "src/components/UI/Upload/PhotoManager";
import RecordNav from "src/components/UI/RecordNav/RecordNav";
import { InfoRegular } from "@fluentui/react-icons";

// Utilities
import {
	filterObjectsByProperty,
	getValuesByPropertyPath,
	getDeepValue,
	mergeArrayById,
	arrayIncludes,
} from "src/utils/dataUtils";
import { Session, isDevTeam, isAdmin } from "src/utils/sessionUtils";
import { debugLog, showTesting } from "src/utils/debugUtils";
import { verifyFields } from "src/utils/formValidationUtils";
import { mColor } from "src/utils/themeUtils";
import { getBaselineItemDataStatusLabel } from "src/utils/baselineUtils";

const getSelectLabel = (item) =>
	item?.label ??
	item?.extended_name ??
	item?.display_name ??
	item?.name ??
	item?.comp_value ??
	(item?.code && item?.symbol ? `${item.code} - ${item.symbol}` : undefined) ??
	item?.code ??
	item?.title ??
	item?.text ??
	String(item?.id ?? "");

const sizeToSelectSize = (size) => {
	if (size === "large") return "md";
	if (size === "small") return "sm";
	return size || "sm";
};

const sizeToFieldControl = (size) => {
	if (size === "large") return "md";
	if (size === "small") return "sm";
	return size || "sm";
};

const parseNumber = (value, fallback = 0) => {
	const parsed = Number(value);
	return Number.isFinite(parsed) ? parsed : fallback;
};

const getSelectValue = (item) => String(item?.id ?? item?.value ?? "");

const buildSelectCollectionItems = (items = []) =>
	items.map((item) => ({
		label: getSelectLabel(item),
		value: getSelectValue(item),
	}));

const renderSelectValueLabel = (selectedItems) => getSelectLabel(selectedItems?.[0]);

const BaselineItemConfigs = (props) => {
	const renderCount = useRef(0);
	renderCount.current++;

	const pageMode = props.mode;
	const uiSize = props.size;
	const { onSetDataChanged } = props;
	const isDisabled = pageMode !== "edit";
	// const formFieldWidth = "60%";
	const baselineProp = props.baseline ?? null;

	// --- Redux Selectors ---
	const selectedBaseline = useSelector((state) => state.baselines.selectedBaseline);
	const preferredBaseline =
		pageMode === "scenario" ? (baselineProp ?? selectedBaseline) : (selectedBaseline ?? baselineProp);

	// --- Local State ---
	const [dataChanged, setDataChanged] = useState(false);
	const [currBaseline, setCurrBaseline] = useState(preferredBaseline);
	const [currentItem, setCurrentItem] = useState(props.item);
	const [nameError, setNameError] = useState("");
	const [militaryServiceError, setMilitaryServiceError] = useState("");
	const [domainError, setDomainError] = useState("");
	const [categoryError, setCategoryError] = useState("");
	const [optionTypeError, setOptionTypeError] = useState("");
	const [osPerUnitError, setOsPerUnitError] = useState("");
	const [osPerYearError, setOsPerYearError] = useState("");
	const [fieldingDelayError, setFieldingDelayError] = useState("");
	const [unitCostExponentError, setUnitCostExponentError] = useState("");
	const [incrementError, setIncrementError] = useState("");
	const [totalExistingInventoryError, setTotalExistingInventoryError] = useState("");
	const [totalExistingInventoryHint, setTotalExistingInventoryHint] = useState("");

	// Custom hooks
	const { lookups } = useLookupsData();

	const { showDialog, withConfirmation } = useDialogBox();

	const { fetchBaselineByIdOrUuid } = useBaselinesData();

	const { createItemAction, updateItemAction, deleteItemAction, copyItemAction, updateItemStatusAction } =
		useBaselineItemActions({
			fetchBaselineByIdOrUuid,
			// clearDataError,
			handleBackToList,
			currBaseline,
			localItem: currentItem,
			setLocalItem: setCurrentItem,
			lookups,
		});

	// --- Effect: sync local baseline and reset dataChanged baseline changes
	useEffect(() => {
		debugLog(2, "[BaselineItemConfigs][FLOW] mounted", {});
	}, []);

	useEffect(() => {
		setCurrBaseline(preferredBaseline);
		setDataChanged(false);
		onSetDataChanged(false);

		debugLog(3, "[BaselineItemConfigs][STATE] selectedBaseline changed", {
			selectedBaseline: selectedBaseline,
			currentItemId: currentItem?.id ?? null,
		});
	}, [currentItem?.id, onSetDataChanged, preferredBaseline, selectedBaseline]);

	const baselineData = preferredBaseline ?? currBaseline ?? baselineProp;
	const baselineConfigs = getDeepValue(baselineData, "baseline_configs", []);

	// --- Effect: sync local when parent item changes
	useUpdateEffect(() => {
		setCurrentItem(props.item); // props.item is already deepCloned in BaselineItems
	}, [props.item]);

	useUpdateEffect(() => {
		if (currentItem.option_type_id !== 0) {
			const options = filterObjectsByProperty(
				lookups.BL_LOOKUPS_ITEM_OPTIONS,
				"group_id",
				currentItem.option_type_id,
			);
			setCurrentItem({
				...currentItem,
				options: options,
				optionIds: getValuesByPropertyPath(options, "lookup_id"),
			});
		}
	}, [currentItem.option_type_id]);

	// Effect: Debugging
	useEffect(() => {
		debugLog(3, "[BaselineItemConfigs][STATE] currentItem changed", {
			itemId: currentItem?.id ?? null,
		});
	}, [currentItem]);

	// --- Utility Functions ---
	const isNew = () => !currentItem?.id; // Prevent running logic if currentItem is null/undefined

	const periodCount = Array.isArray(baselineData?.cfg_period_label)
		? baselineData.cfg_period_label.length
		: parseNumber(baselineData?.number_of_period, 0);
	const fieldingDelayMax = Math.max(0, periodCount - 1);
	const detailPanelMinHeight = currentItem?.id ? "22rem" : "16rem";

	const validators = useMemo(
		() => [
			{
				field: "name",
				validate: (v) => v !== "",
				setError: setNameError,
				message: "Name is required!",
			},
			{
				field: "military_service_id",
				validate: (v) =>
					v === "" ||
					arrayIncludes(
						getValuesByPropertyPath(
							filterObjectsByProperty(baselineConfigs, "comp_type_id", 111),
							"id",
						),
						v,
					),
				setError: setMilitaryServiceError,
				message: "Military Service is invalid.",
			},
			{
				field: "domain_id",
				validate: (v) =>
					v === "" ||
					arrayIncludes(
						getValuesByPropertyPath(
							filterObjectsByProperty(baselineConfigs, "comp_type_id", 112),
							"id",
						),
						v,
					),
				setError: setDomainError,
				message: "Domain is invalid.",
			},
			{
				field: "category_id",
				validate: (v) =>
					v === "" ||
					arrayIncludes(
						getValuesByPropertyPath(
							filterObjectsByProperty(baselineConfigs, "comp_type_id", 113),
							"id",
						),
						v,
					),
				setError: setCategoryError,
				message: "Category is invalid.",
			},
			{
				field: "option_type_id",
				validate: (v) => v !== "",
				setError: setOptionTypeError,
				message: "Option Type is required!",
			},
			{
				field: "os_cost_per_unit",
				validate: (v) => v === "" || (validator.isDecimal(v) && validator.isFloat(v, { min: 0 })),
				setError: setOsPerUnitError,
				message: "Value must be a decimal number >= 0!",
			},
			{
				field: "fixed_os_cost_per_year",
				validate: (v) => v === "" || (validator.isDecimal(v) && validator.isFloat(v, { min: 0 })),
				setError: setOsPerYearError,
				message: "Value must be a decimal number >= 0!",
			},
			{
				field: "fielding_delay",
				validate: (v) => v === "" || validator.isInt(v, { min: 0, max: fieldingDelayMax }),
				setError: setFieldingDelayError,
				message: `Value must be an integer between 0 and ${fieldingDelayMax}!`,
			},
			{
				field: "unit_cost_exponent",
				validate: (v) =>
					v === "" ||
					(validator.isFloat(v, { min: 0, max: 1 }) &&
						/^(?:0(?:\.\d{1,2})?|1(?:\.0{1,2})?)$/.test(v)),
				setError: setUnitCostExponentError,
				message: "Value must be a decimal number between 0 and 1!",
			},
			{
				field: "increment",
				validate: (v) => v === "" || validator.isInt(v, { min: 0 }),
				setError: setIncrementError,
				message: "Value must be an integer >= 0!",
			},
			{
				field: "total_existing_inventory",
				validate: (v) => v === "" || validator.isInt(v, { min: 0 }),
				setError: setTotalExistingInventoryError,
				message: "Value must be an integer >= 0!",
			},
		],
		[baselineConfigs, fieldingDelayMax],
	);
	const verifyDataFields = useCallback((data) => {
		if (!data) return true; // Treat as error if item is missing
		const errorFound = verifyFields(data, validators);

		const incrementRaw = (data.increment ?? "").toString().trim();
		const totalInventoryRaw = (data.total_existing_inventory ?? "").toString().trim();
		const incrementValue = parseNumber(incrementRaw, NaN);
		const totalInventoryValue = parseNumber(totalInventoryRaw, NaN);
		const invalidCrossField =
			incrementRaw !== "" &&
			totalInventoryRaw !== "" &&
			Number.isFinite(incrementValue) &&
			Number.isFinite(totalInventoryValue) &&
			totalInventoryValue < incrementValue;
		const missingInventory = totalInventoryRaw === "";

		if (invalidCrossField) {
			setTotalExistingInventoryError(
				"Total # of increments in inventory must be greater than or equal to Increment Size!",
			);
			setTotalExistingInventoryHint("");
		} else if (missingInventory) {
			setTotalExistingInventoryError("");
			// Keep empty-field guidance visible while allowing draft saves.
			setTotalExistingInventoryHint(
				"Total # of increments in inventory is empty. The item can be saved and will stay blank, but it will remain Partial until completed.",
			);
		} else {
			setTotalExistingInventoryError("");
			setTotalExistingInventoryHint("");
		}

		return errorFound || invalidCrossField;
	}, [validators]);

	useEffect(() => {
		if (!currentItem) return;

		// Keep the visible error state in sync with the currently loaded item so
		// new-item screens and record switches show the same initial validation
		// styling that these fields already use after the user starts editing.
		verifyDataFields(currentItem);
	}, [currentItem, verifyDataFields]);

	const prepareFieldsForOptiionType = (selected) => {
		const options = filterObjectsByProperty(lookups.BL_LOOKUPS_ITEM_OPTIONS, "group_id", selected);

		const updateData = {
			...currentItem,
			option_type_id: selected,
			options: options,
			optionIds: getValuesByPropertyPath(options, "lookup_id"),
			update_flag: currentItem.id ? 102 : 101,
			updatedAt: new Date(),
			updatedBy: Session().currUser.id,
		};

		debugLog(4, "[BaselineItemConfigs][PROCESS] prepareFieldsForOptiionType", { selected, updateData });

		setCurrentItem(updateData);
		props.onLocalItemChange(updateData);

		props.onClearComplete();
	};

	// All changes to baseline_item_configs go through mergeArrayById to ensure no accidental duplicates.
	const addValueToSelectedOptionUpdateHandler = (items, removeItem = null) => {
		debugLog(3, "[BaselineItemConfigs][ACTION] addValueToSelectedOptionUpdateHandler", {
			items: items,
			removeItem: removeItem,
		});
		debugLog(4, "[BaselineItemConfigs][PROCESS] addValueToSelectedOptionUpdateHandler", {
			items: items,
			removeItem: removeItem,
		});

		/* // with the use of mergeArrayById() 

			// Remove the item with removeId from baseline_item_configs
			let arrayItemConfigs = currentItem.baseline_item_configs;
			if (removeItem && removeItem.id !== undefined) {
				arrayItemConfigs = arrayItemConfigs.filter((x) => x.id !== removeItem.id);
			}

			// Replace/add configs with update_flag 101 or 102, removing any prior with same id
			// items.map((x) => {
			// 	if (x.update_flag === 101 || x.update_flag === 102) {
			// 		// find/replace the object; keep the updated one at the end
			// 		arrayItemConfigs = arrayItemConfigs.filter((y) => {
			// 			return y.id !== x.id;
			// 		});
			// 		arrayItemConfigs.push(x);
			// 	}
			// 	return false;
			// });
			const updatedConfigs = items
				// Keeps only items with update_flag 101 or 102.
				.filter((x) => x.update_flag === 101 || x.update_flag === 102)
				// Take arrayItemConfigs (the original array) as the initial accumulator (acc).
				// For each filtered item x, remove existing with same id, then add x
				.reduce((acc, x) => {
					// if x.id already exists in acc, it gets replaced by x. If not, x is simply appended.
					return [...acc.filter((y) => y.id !== x.id), x];
				}, arrayItemConfigs);

			const updateData = {
				...currentItem,
				forceStructures: items,
				baseline_item_configs: updatedConfigs,
				update_flag: currentItem.id ? 102 : 101,
				updatedAt: new Date(),
				updatedBy: Session().currUser.id,
			};

		*/

		const filteredNewConfigs = items.filter((x) => x.update_flag === 101 || x.update_flag === 102);

		// If removeItem is used and not covered by items, handle removal first
		let initialArray = currentItem.baseline_item_configs;
		if (removeItem && removeItem.id !== undefined) {
			initialArray = initialArray.filter((x) => x.id !== removeItem.id);
		}

		const mergedItem = mergeArrayById(
			{ ...currentItem, baseline_item_configs: initialArray },
			"baseline_item_configs",
			filteredNewConfigs,
			"id",
		);

		const updateData = {
			...mergedItem,
			forceStructures: items,
			update_flag: currentItem.id ? 102 : 101,
			updatedAt: new Date(),
			updatedBy: Session().currUser.id,
		};

		setCurrentItem(updateData);
		props.onLocalItemChange(updateData);
		props.onSetDataChanged(true);
	};

	const clearCurrentItem = () => {
		// props.onSetCurrentItem(null);
		props.onClearCurrentItem();
	};

	// --- Handlers ---
	function handleBackToList() {
		debugLog(3, "[BaselineItemConfigs][ACTION] handleBackToList", {});

		if (dataChanged) {
			withConfirmation(
				{
					title: "Warning",
					message: [
						"The data is changed, and it'd be discarded if you choose to continue and save.",
						" ",
						"Do you want to proceed?",
					],
				},
				() => clearCurrentItem(),
			);
		} else {
			clearCurrentItem();
		}
	}

	const handleInputChange = (event) => {
		const { name, value } = event.target;

		debugLog(7, "[BaselineItemConfigs][ACTION] handleInputChange", { name: name, value: value });

		const updateData = {
			...currentItem,
			[name]: value,
			update_flag: currentItem.id ? 102 : 101,
			updatedAt: new Date(),
			updatedBy: Session().currUser.id,
		};

		setCurrentItem(updateData);
		setDataChanged(true);
		props.onSetDataChanged(true);

		// skip below if errorFound
		const errorFound = verifyDataFields(updateData);
		if (errorFound) return;

		// Notify parent component of changes.
		props.onLocalItemChange(updateData);

		// props.onClearComplete();
	};

	const changeValueHandler = (name, selected) => {
		debugLog(3, "[BaselineItemConfigs][ACTION] changeValueHandler", { name: name, selected: selected });

		const updateData = {
			...currentItem,
			[name]: selected,
			update_flag: currentItem.id ? 102 : 101,
			updatedAt: new Date(),
			updatedBy: Session().currUser.id,
		};
		setCurrentItem(updateData);
		props.onLocalItemChange(updateData);
		setDataChanged(true);
		props.onSetDataChanged(true);

		verifyDataFields(updateData);
	};

	const photoUpdatedHandler = () => {
		debugLog(3, "[BaselineItemConfigs][ACTION] photoUpdatedHandler", {
			baselineId: currentItem?.baseline_id,
			itemId: currentItem?.id,
		});
		debugLog(2, "[BaselineItemConfigs][FLOW] photoUpdated reload started", {
			baselineId: currentItem?.baseline_id,
			itemId: currentItem?.id,
		});
		// reload the BaselineItemList
		fetchBaselineByIdOrUuid(currentItem.baseline_id);

		// reload currentItem
		props.onReloadCurrentItem(currentItem);
	};

	const updateItemConfigHandler = (item) => {
		debugLog(3, "[BaselineItemConfigs][ACTION] updateItemConfigHandler", { item: item });

		const arrayId = getValuesByPropertyPath(item, "id");
		const newConfigs = [
			...item, // updated configs from the child
			...currentItem.baseline_item_configs.filter((x) => !arrayId.includes(x.id)),
		];
		const updateData = {
			...currentItem,
			baseline_item_configs: newConfigs,
			update_flag: currentItem.id ? 102 : 101,
			updatedAt: new Date(),
			updatedBy: Session().currUser.id,
		};
		setCurrentItem(updateData);
		props.onLocalItemChange(updateData);
		setDataChanged(true);
		props.onSetDataChanged(true);
	};

	const handleClearComplete = () => {
		props.onClearComplete();
	};

	// When the option type changes, this will both update the field and (if confirmed) reset any configs
	// not compatible with the new type. Actual DB update logic is handled in useBaselineItemActions.
	const optionTypeChangeHandler = (name, selected) => {
		debugLog(3, "[BaselineItemConfigs][ACTION] optionTypeChangeHandler", {
			name: name,
			selected: selected,
		});

		if (selected !== "") setOptionTypeError("");

		// setCurrentItem(prev => ({ ...prev, [name]: selected }));

		if (currentItem?.id) {
			withConfirmation(
				{
					title: "Warning",
					message: [
						"You'd changed the Option Type.",
						"Some data will be deleted if you choose to continue and save.",
						" ",
						"Do you want to proceed?",
					],
				},
				// Confirmed
				() => {
					prepareFieldsForOptiionType(selected);
					props.onSetDataChanged(true);
				},
				// Canceled
				() => {
					debugLog(3, "[BaselineItemConfigs][ACTION] optionTypeChangeHandler.beforeReset", {
						option_type_id: currentItem.option_type_id,
						option_type_id_prev: currentItem.option_type_id_prev,
					});

					setCurrentItem((prev) => ({
						...prev,
						option_type_id: prev.option_type_id_prev ?? "",
					}));
				},
			);
		} else {
			prepareFieldsForOptiionType(selected);
		}
	};

	const handleUpdateStatus = (event, newStatus) => {
		// event.preventDefault();

		debugLog(3, "[BaselineItemConfigs][ACTION] handleUpdateStatus", {
			event: event,
			newStatus: newStatus,
		});

		let arrayMsg = [
			`You are about to archive this Item, ${currentItem.name}.`,
			"It would be removed from the Baseline for the general users.",
			" ",
			"Do you want to proceed?",
		];
		if (newStatus === -1) {
			arrayMsg = [
				`You are about to permanently DELETE this Item, ${currentItem.name}.`,
				"The data will be removed from the application.",
				"This action CANNOT be undone.",
				" ",
				"Do you want to proceed?",
			];
		}

		if (newStatus !== 1) {
			withConfirmation(
				{
					title: "Warning",
					message: arrayMsg,
				},
				() => updateItemStatusAction(currentItem.id, newStatus),
			);
		} else {
			updateItemStatusAction(currentItem.id, newStatus);
		}
	};

	const handleCopyItem = () => {
		debugLog(3, "[BaselineItemConfigs][ACTION] handleCopyItem", { currentItem: currentItem });
		withConfirmation(
			{
				title: "Warning",
				message: [
					`You are about to copy this item, ${currentItem.name}.`,
					" ",
					"Do you want to proceed?",
				],
			},
			() => copyItemAction(currentItem.id),
		);
	};

	const handleDeleteItem = () => {
		debugLog(3, "[BaselineItemConfigs][ACTION] handleDeleteItem", { currentItem: currentItem });
		withConfirmation(
			{
				title: "Warning",
				message: [
					`You are about to permanently PURGE this Item, ${currentItem.name}.`,
					"The data will be removed from the database.",
					"This action CANNOT be undone.",
					" ",
					"Do you want to proceed?",
				],
			},
			() => deleteItemAction(currentItem.id),
		);
	};

	const clickBackToListHandler = (option) => {
		debugLog(3, "[BaselineItemConfigs][ACTION] clickBackToListHandler", { option: option });
		handleBackToList();
	};

	const clickSaveHandler = (option) => {
		debugLog(3, "[BaselineItemConfigs][ACTION] clickSaveHandler", { option: option });

		if (verifyDataFields(currentItem)) {
			showDialog({
				title: "Error",
				message: ["Invalid input", " ", "Please provide all required data to continue."],
				type: "error",
			});
			return false;
		}

		if (isNew()) {
			debugLog(2, "[BaselineItemConfigs][FLOW] createItemAction started", {});
			createItemAction();
		} else {
			debugLog(2, "[BaselineItemConfigs][FLOW] updateItemAction started", {});
			updateItemAction();
		}
	};

	const clickArchiveHandler = (option, newStatus) => {
		debugLog(3, "[BaselineItemConfigs][ACTION] clickArchiveHandler", {
			option: option,
			newStatus: newStatus,
		});
		handleUpdateStatus(option, newStatus);
	};

	const clickPurgeHandler = (option) => {
		debugLog(3, "[BaselineItemConfigs][ACTION] clickPurgeHandler", { option: option });
		handleDeleteItem(option, 1);
	};

	const clickDeleteHandler = (option) => {
		debugLog(3, "[BaselineItemConfigs][ACTION] clickDeleteHandler", { option: option });
		handleUpdateStatus(option, -1);
	};

	const clickCopyHandler = (option) => {
		debugLog(3, "[BaselineItemConfigs][ACTION] clickCopyHandler", { option: option });
		handleCopyItem(option, 1);
	};

	// Keep the field-level error text easy to reuse across the direct v2-native selects.
	const nameProps = nameError ? { error: true, helperText: nameError } : {};
	// Missing membership keeps the item saveable as Unresolved, so show the red
	// field guidance here without feeding that state back into the blocking validator path.
	const militaryServiceProps = militaryServiceError
		? { error: true, helperText: militaryServiceError }
		: !String(currentItem?.military_service_id ?? "").trim()
			? { error: true, helperText: "Military Service is required." }
			: {};
	const domainProps = domainError
		? { error: true, helperText: domainError }
		: !String(currentItem?.domain_id ?? "").trim()
			? { error: true, helperText: "Domain is required." }
			: {};
	const categoryProps = categoryError
		? { error: true, helperText: categoryError }
		: !String(currentItem?.category_id ?? "").trim()
			? { error: true, helperText: "Category is required." }
			: {};
	const osPerUnitProps = osPerUnitError ? { error: true, helperText: osPerUnitError } : {};
	const osPerYearProps = osPerYearError ? { error: true, helperText: osPerYearError } : {};
	const unitCostExponentProps = unitCostExponentError
		? { error: true, helperText: unitCostExponentError }
		: {};
	const fieldingDelayProps = fieldingDelayError ? { error: true, helperText: fieldingDelayError } : {};
	const incrementProps = incrementError ? { error: true, helperText: incrementError } : {};
	const totalExistingInventoryProps = totalExistingInventoryError
		? { error: true, helperText: totalExistingInventoryError, helperTone: "error" }
		: totalExistingInventoryHint
			? { error: true, helperText: totalExistingInventoryHint, helperTone: "error" }
			: {};
	const optionTypeProps = optionTypeError ? { error: true, helperText: optionTypeError } : {};
	const dataStatusLabel = getBaselineItemDataStatusLabel(currentItem?.data_status_id);

	// Helper objects for UI - dropdown
	const validServiceIds = getValuesByPropertyPath(
		filterObjectsByProperty(baselineConfigs, "comp_type_id", 111),
		"id",
	);
	const selectedServiceId = validServiceIds.includes(currentItem?.military_service_id)
		? currentItem.military_service_id
		: "";

	const validDomainIds = getValuesByPropertyPath(
		filterObjectsByProperty(baselineConfigs, "comp_type_id", 112),
		"id",
	);
	const selectedDomainId = validDomainIds.includes(currentItem?.domain_id) ? currentItem.domain_id : "";

	const validCategoryIds = getValuesByPropertyPath(
		filterObjectsByProperty(baselineConfigs, "comp_type_id", 113),
		"id",
	);
	const selectedCategoryId = validCategoryIds.includes(currentItem?.category_id)
		? currentItem.category_id
		: "";
	// Baseline item plain-text fields now share the stable direct-v2 helper while
	// the numeric/select helpers stay local to preserve item-detail behavior.
	const renderTextField = (props) => (
		<FormTextField {...props} size={sizeToFieldControl(uiSize)} helperColor={mColor.Danger} />
	);

	// Keep the numeric helper local because these item-detail fields still carry
	// screen-specific currency formatting, tooltips, and validation messaging.
	const renderNumericField = ({
		id,
		name,
		label,
		value,
		onChange,
		disabled = false,
		required = false,
		errorText = "",
		helperTone = "error",
		width = "100%",
		endAdornment,
		formatSpec,
	}) => {
		const invalid = helperTone === "error" && Boolean(errorText);
		const helpId = `${id}-helptext`;
		const controlSize = sizeToFieldControl(uiSize);
		const helperVisible = Boolean(errorText);
		const helperColor = invalid ? mColor.Danger : helperTone === "info" ? mColor.NavyBlue : "inherit";
		const controlStyle = {
			width: "100%",
			fontSize: controlSize === "sm" ? "0.8rem" : "0.875rem",
			paddingRight: endAdornment ? 38 : undefined,
		};

		return (
			<Box width={width} mt="1rem" mb="0.5rem">
				<Field.Root required={required} invalid={invalid} style={{ width: "100%" }}>
					<Field.Label htmlFor={id}>
						{label}
						{required ? <Field.RequiredIndicator /> : null}
					</Field.Label>
					<div style={{ position: "relative", width: "100%" }}>
						{/* Keep the existing raw numeric-string onChange contract by wiring the
						    formatter directly while the v2 Field shell owns label/helper layout. */}
						<NumericFormatAdapter
							customInput={Input}
							id={id}
							name={name}
							value={value}
							disabled={disabled}
							size={controlSize}
							variant="subtle"
							aria-invalid={invalid ? "true" : undefined}
							aria-describedby={helpId}
							onChange={onChange}
							formatSpec={formatSpec}
							style={controlStyle}
						/>
						{endAdornment ? (
							<span
								style={{
									position: "absolute",
									right: 8,
									top: "50%",
									transform: "translateY(-50%)",
									display: "inline-flex",
									alignItems: "center",
									pointerEvents: "auto",
								}}
							>
								{endAdornment}
							</span>
						) : null}
					</div>
					<Field.HelperText id={helpId}>
						<span
							style={{
								display: "block",
								minHeight: "1.5em",
								fontSize: "0.75rem",
								color: helperColor,
								visibility: helperVisible ? "visible" : "hidden",
							}}
						>
							{helperVisible ? errorText : "\u00A0"}
						</span>
					</Field.HelperText>
				</Field.Root>
			</Box>
		);
	};
	// These item-detail selects all share the same error/helper and numeric-id behavior.
	const renderSingleSelectField = ({
		id,
		name,
		label,
		items,
		selectedItem,
		placeholder = "Select...",
		errorText,
		required = false,
		disabled = false,
		onChangeValue = changeValueHandler,
		width = props.formFieldWidth || "100%",
	}) => {
		const invalid = Boolean(errorText);
		const hasHelperContent = Boolean(errorText);
		const helpId = `${id}-helptext`;
		const wantsNumber =
			typeof selectedItem === "number" || items.some((item) => typeof item?.id === "number");
		const currentValue = selectedItem == null ? "" : String(selectedItem);
		const collection = createListCollection({
			// Keep these Baseline item-detail menus to real lookup choices only.
			// This screen does not need an explicit clear-to-empty option in the dropdown.
			items: buildSelectCollectionItems(items),
		});

		return (
			<Box width={width} mt="1rem" mb="0.5rem">
				<Field.Root required={required} invalid={invalid} style={{ width: "100%" }}>
					<SelectRoot
						name={name}
						collection={collection}
						size={sizeToSelectSize(uiSize)}
						width="100%"
						variant="outline"
						invalid={invalid}
						disabled={disabled}
						value={currentValue ? [currentValue] : []}
						onValueChange={(details) => {
							// Preserve the existing item-config field contract by translating the
							// direct Select value array back to the same string/number shape the
							// local change handlers already expect.
							const raw = Array.isArray(details?.value) ? String(details.value[0] ?? "") : "";
							const nextValue = raw === "" ? "" : wantsNumber ? Number(raw) : String(raw);
							onChangeValue(name, nextValue);
						}}
					>
						<SelectLabel>
							{label}
							{required ? <Field.RequiredIndicator /> : null}
						</SelectLabel>
						<SelectTrigger
							id={id}
							aria-invalid={invalid ? "true" : undefined}
							aria-describedby={helpId}
						>
							<SelectValueText placeholder={placeholder}>
								{renderSelectValueLabel}
							</SelectValueText>
						</SelectTrigger>
						<Portal>
							<SelectContent>
								{collection.items.map((item) => (
									<SelectItem key={item.value} item={item}>
										{item.label}
									</SelectItem>
								))}
							</SelectContent>
						</Portal>
					</SelectRoot>
					<Field.HelperText id={helpId}>
						<span
							style={{
								display: "block",
								minHeight: "1.5em",
								fontSize: "0.75rem",
								color: invalid ? mColor.Danger : "inherit",
								visibility: hasHelperContent ? "visible" : "hidden",
							}}
						>
							{hasHelperContent ? errorText : "\u00A0"}
						</span>
					</Field.HelperText>
				</Field.Root>
			</Box>
		);
	};

	const sortedConfigsByLookupId = React.useMemo(() => {
		// Build an object: { [lookup_id]: sortedArray }
		const result = {};
		if (currentItem?.baseline_item_configs && currentItem?.options) {
			currentItem.options.forEach((option) => {
				const configs = filterObjectsByProperty(
					currentItem.baseline_item_configs,
					"comp_type_id",
					option.lookup_id,
				);
				result[option.lookup_id] = [...configs].sort((x, y) => x.sort_id - y.sort_id);
			});
		}
		return result;
	}, [currentItem]);

	// --- Debug Log: Render and State Snapshots ---
	// debugLog(2, "\n↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓\n");
	debugLog(9, "[BaselineItemConfigs][FLOW] render", {
		renderCount: renderCount.current,
		currentItem: currentItem,
	});
	debugLog(9, "[BaselineItemConfigs][FLOW] renderState", {
		props: props,
		currBaseline: currBaseline,
		currentItem: currentItem,
		dataChanged: dataChanged,
		nameError: nameError,
		militaryServiceError: militaryServiceError,
		domainError: domainError,
		categoryError: categoryError,
		optionTypeError: optionTypeError,
	});
	// debugLog(4, "\n↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑\n");

	return (
		<Box data-testid="BaselineItemConfigs">
			{currentItem && (
				<>
					<RecordNav
						data={props.items}
						callFrom="Baseline Item"
						showClose
						{...(pageMode === "edit"
							? {
									...(showTesting() && isDevTeam() ? { showPurge: true } : {}),
									...(isAdmin() || isDevTeam() ? { showDelete: true } : {}),
									...(pageMode === "edit"
										? {
												showCopy: true,
												...(currentItem.status_id !== 1
													? { showRestore: true }
													: { showArchive: true }),
												showSave: true,
												...(dataChanged ? { showDataChangeAlert: true } : {}),
											}
										: {}),
								}
							: {})}
						onClickBackToList={clickBackToListHandler}
						onClickPurge={clickPurgeHandler}
						onClickDelete={clickDeleteHandler}
						onClickCopy={clickCopyHandler}
						onClickArchive={clickArchiveHandler}
						onClickSave={clickSaveHandler}
					/>

					<Box width="100%" display="flex" alignItems="flex-start" gap={2}>
						<Box flex="1 1 60%">
							{renderTextField({
								id: "name",
								dataTestId: "item-name-input",
								name: "name",
								label: "Item Name",
								required: true,
								value: currentItem?.name ?? "",
								maxLength: 256,
								onChange: handleInputChange,
								disabled: isDisabled,
								errorText: nameProps.helperText,
								marginBottom: 0,
							})}
						</Box>
						<Box flex="1 1 40%">
							{renderTextField({
								id: "data_status_id",
								name: "data_status_id",
								label: "Data Status",
								value: dataStatusLabel,
								disabled: true,
								reserveHelperSpace: false,
								marginBottom: 0,
							})}
						</Box>
					</Box>

					<Box width="100%" display="flex" alignItems="stretch" gap={2}>
						<Box flex="1 1 60%">
							{renderTextField({
								id: "description",
								name: "description",
								label: "Description",
								multiline: true,
								rows: 12,
								maxLength: 4096,
								value: currentItem?.description ?? "",
								onChange: handleInputChange,
								disabled: isDisabled,
								// Keep the description and photo panels on the same vertical rhythm.
								minControlHeight: detailPanelMinHeight,
								reserveHelperSpace: false,
								marginTop: "1rem",
							})}
						</Box>
						<Box
							flex="1 1 40%"
							display="flex"
							flexDirection="column"
							mt="1rem"
							minHeight={detailPanelMinHeight}
						>
							<Field.Root style={{ width: "100%" }}>
								<Field.Label>Photo</Field.Label>
							</Field.Root>
							{!currentItem.id ? (
								renderTextField({
									id: "photo",
									name: "photo",
									label: "",
									placeholder: "Photo will be available after the item is created.",
									disabled: true,
									endAdornment: (
										<MetreaTooltip
											content="You'll be able to upload/change photo after the item was created."
										>
											<Icon as={InfoRegular} boxSize="24px" color={mColor.StonyBlue} />
										</MetreaTooltip>
									),
									marginTop: "0.35rem",
									reserveHelperSpace: false,
								})
							) : (
								<Box flex="1" display="flex" minHeight={detailPanelMinHeight} mt="0.35rem">
									<PhotoManager
										item={currentItem}
										editable={pageMode === "edit"}
										table_name="baseline_items"
										title=""
										showHeader={false}
										containerStyle={{
											margin: 0,
											height: "100%",
											minHeight: detailPanelMinHeight,
										}}
										// Trigger data reload from backend
										onPhotoUpdated={photoUpdatedHandler}
									/>
								</Box>
							)}
						</Box>
					</Box>

					<HStack padding={0} paddingTop={1} gap={2} alignItems="flex-start">
						<Box flex="1" marginRight={8}>
							{renderSingleSelectField({
								id: "military_service_id",
								name: "military_service_id",
								required: true,
								label: "Military Service",
								items: filterObjectsByProperty(baselineConfigs, "comp_type_id", 111),
								selectedItem: selectedServiceId,
								errorText: militaryServiceProps.helperText,
								disabled: isDisabled,
							})}
						</Box>
						<Box flex="1" marginRight={8}>
							{renderSingleSelectField({
								id: "domain_id",
								name: "domain_id",
								required: true,
								label: "Domain",
								items: filterObjectsByProperty(baselineConfigs, "comp_type_id", 112),
								selectedItem: selectedDomainId,
								errorText: domainProps.helperText,
								disabled: isDisabled,
							})}
						</Box>
						<Box flex="1">
							{renderSingleSelectField({
								id: "category_id",
								name: "category_id",
								required: true,
								label: "Category",
								items: filterObjectsByProperty(baselineConfigs, "comp_type_id", 113),
								selectedItem: selectedCategoryId,
								errorText: categoryProps.helperText,
								disabled: isDisabled,
							})}
						</Box>
					</HStack>

					<HStack padding={0} paddingTop={1} gap={2}>
						<Box flex="1" marginRight={8}>
							{renderNumericField({
								id: "os_cost_per_unit",
								name: "os_cost_per_unit",
								label: "O&S Cost per Increment",
								value: currentItem?.os_cost_per_unit ?? "",
								onChange: handleInputChange,
								disabled: isDisabled,
								errorText: osPerUnitProps.helperText,
								endAdornment: (
									<MetreaTooltip
										content="This is the marginal O&S cost for each increment of this item that is added or cut from the force. Enter the cost in inflation adjusted dollars indexed to the first period of the baseline."
									>
										<Icon as={InfoRegular} boxSize="24px" color={mColor.StonyBlue} />
									</MetreaTooltip>
								),
								formatSpec: {
									kind: "currency",
									symbol: currBaseline?.currency_symbol ?? "",
									unit: currBaseline?.currency_unit ?? "",
									decimals: 2,
									fixed: true,
									allowNegative: false,
								},
							})}
						</Box>
						<Box flex="1" marginRight={8}>
							{renderNumericField({
								id: "fixed_os_cost_per_year",
								name: "fixed_os_cost_per_year",
								label: "Fixed O&S for the Total Fleet",
								value: currentItem?.fixed_os_cost_per_year ?? "",
								onChange: handleInputChange,
								disabled: isDisabled,
								errorText: osPerYearProps.helperText,
								endAdornment: (
									<MetreaTooltip
										content="This is the total fixed O&S cost associated with this type of item--i.e., the O&S cost that does not vary with the number of items in the inventory. Enter the cost in inflation adjusted dollars indexed to the first period of the baseline."
									>
										<Icon as={InfoRegular} boxSize="24px" color={mColor.StonyBlue} />
									</MetreaTooltip>
								),
								formatSpec: {
									kind: "currency",
									symbol: currBaseline?.currency_symbol ?? "",
									unit: currBaseline?.currency_unit ?? "",
									decimals: 2,
									fixed: true,
									allowNegative: false,
								},
							})}
						</Box>
						<Box flex="1">
							{renderNumericField({
								id: "unit_cost_exponent",
								name: "unit_cost_exponent",
								label: "Unit Cost Exponent",
								value: currentItem?.unit_cost_exponent ?? "",
								onChange: handleInputChange,
								disabled: isDisabled,
								errorText: unitCostExponentProps.helperText,
								endAdornment: (
									<MetreaTooltip
										content="Use this exponent value for items whose overall costs do not scale linearly with the number of increments added or cut."
									>
										<Icon as={InfoRegular} boxSize="24px" color={mColor.StonyBlue} />
									</MetreaTooltip>
								),
								formatSpec: {
									kind: "number",
									decimals: 2,
									fixed: false,
									allowNegative: false,
								},
							})}
						</Box>
					</HStack>
					<HStack padding={0} paddingTop={1} gap={2}>
						<Box flex="1" marginRight={8}>
							{renderNumericField({
								id: "fielding_delay",
								name: "fielding_delay",
								label: "Fielding delay (# of periods)",
								value: currentItem?.fielding_delay ?? "",
								onChange: handleInputChange,
								disabled: isDisabled,
								errorText: fieldingDelayProps.helperText,
								formatSpec: {
									kind: "number",
									decimals: 0,
									fixed: false,
									allowNegative: false,
								},
							})}
						</Box>
						<Box flex="1" marginRight={8}>
							{renderNumericField({
								id: "increment",
								name: "increment",
								label: "Increment Size (# of items per increment)",
								value: currentItem?.increment ?? "",
								onChange: handleInputChange,
								disabled: isDisabled,
								errorText: incrementProps.helperText,
								formatSpec: {
									kind: "number",
									decimals: 0,
									fixed: false,
									allowNegative: false,
								},
							})}
						</Box>
						<Box flex="1">
							{renderNumericField({
								id: "total_existing_inventory",
								name: "total_existing_inventory",
								label: "Total # of Increments in Inventory",
								value: currentItem?.total_existing_inventory ?? "",
								onChange: handleInputChange,
								disabled: isDisabled,
								errorText: totalExistingInventoryProps.helperText,
								helperTone: totalExistingInventoryProps.helperTone,
								formatSpec: {
									kind: "number",
									decimals: 0,
									fixed: false,
									allowNegative: false,
								},
							})}
						</Box>
					</HStack>

					{currentItem.forceStructures && (
						<AddValueToSelectedOption
							currItem={currentItem}
							value={currentItem.forceStructures}
							defaultValue={"1"}
							options={getDeepValue(currBaseline, "cfg_force_structure_metrics", [])}
							mode={pageMode}
							size={uiSize || "small"}
							label="Force Structure Metric"
							width={props.formFieldWidth}
							onUpdate={addValueToSelectedOptionUpdateHandler}
						/>
					)}

					<Box width="50%" marginBottom={20} display="flex" gap={2} alignItems="center">
						{renderSingleSelectField({
							id: "option_type_id",
							name: "option_type_id",
							label: "Option Type",
							items: filterObjectsByProperty(lookups.BL_ITEM_OPT_TYPES, "group_id", 300),
							selectedItem: currentItem?.option_type_id ?? "",
							errorText: optionTypeProps.helperText,
							onChangeValue: optionTypeChangeHandler,
							disabled:
								isDisabled ||
								(!isNew() &&
									!isDevTeam() &&
									getDeepValue(currBaseline, "scenarios.length", 0) > 0),
							width: props.formFieldWidth,
						})}
						<MetreaTooltip
							content="Option Type can't be changed if there's any associated scenarios."
						>
							<Icon as={InfoRegular} marginTop="2rem" boxSize="24px" color={mColor.StonyBlue} />
						</MetreaTooltip>
					</Box>

					{/* Keep the previous two-step wrapper spacing here after moving to direct v2 Stack. */}
					<Stack gap="16px">
						{currentItem.option_type_id !== 0 &&
							currentItem.options &&
							currentItem.options.length !== 0 &&
							currentItem.options.map((option, index) => {
								const sortedConfigs = sortedConfigsByLookupId[option.lookup_id] || [];

								return (
									<div key={option.id}>
										<BaselineItemConfigPeriod
											mode={pageMode}
											size={uiSize || "small"}
											type={option}
											periodLabel={currBaseline?.cfg_period_label ?? ""}
											item={sortedConfigs}
											currencySymbol={currBaseline?.currency_symbol ?? ""}
											currencyUnit={currBaseline?.currency_unit ?? ""}
											onUpdate={updateItemConfigHandler}
											onClearComplete={handleClearComplete}
										/>
									</div>
								);
							})}
						{currentItem.option_type_id === 0 && <>New Item. Please save first.</>}
					</Stack>
				</>
			)}
		</Box>
	);
};

export default BaselineItemConfigs;
